import { useState } from "react";
import { Loader2, AlertTriangle, ArrowRight, Info, ArrowLeft } from "lucide-react";
import {
  signInWithMicrosoft,
  isEmailAllowed,
  ALLOWED_DOMAINS
} from "@services/auth";
import type { AuthSession } from "@/types/user";

interface LoginPageProps {
  onAuth: (session: AuthSession) => void;
}

type Stage = "email" | "signing-in" | "error";

const TENANT_HINT = (
  import.meta.env.VITE_ENTRA_TENANT_ID || "f7615ef4-549b-4a4c-9318-017933c06a60"
).slice(0, 8);

export function LoginPage({ onAuth }: LoginPageProps) {
  const [stage, setStage] = useState<Stage>("email");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [errorCode, setErrorCode] = useState("");

  function validateEmail(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) return "Enter your email address";
    if (!trimmed.includes("@") || !trimmed.includes(".")) return "Enter a valid email address";
    if (!isEmailAllowed(trimmed)) {
      return `Sign-in is restricted to ${ALLOWED_DOMAINS.join(" or ")} email addresses`;
    }
    return "";
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const err = validateEmail(email);
    if (err) {
      setEmailError(err);
      return;
    }
    setEmailError("");
    setStage("signing-in");
    setErrorMsg("");
    setErrorCode("");

    try {
      const session = await signInWithMicrosoft(email.trim());
      if (session) {
        // Shouldn't happen in pure-redirect flow, but kept as a safety net
        // in case signInWithMicrosoft is ever swapped back to popup mode.
        onAuth(session);
      } else {
        // Pure-redirect flow: signInWithMicrosoft returns null because the
        // tab is navigating away to login.microsoftonline.com. Keep the
        // spinner up — the navigation will replace the page momentarily.
        // After the user signs in and returns, initializeMsal()'s
        // handleRedirectPromise picks up the session and useAuth surfaces
        // it to App.tsx, which routes past LoginPage automatically.
      }
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string };
      const code = e?.code || "";
      const msg = e?.message || String(err);

      let friendly = msg;
      if (msg.includes("AADSTS50011") || msg.toLowerCase().includes("redirect uri")) {
        friendly = `Redirect URI mismatch. Add this URL to your Entra app registration → Authentication → Single-page application: ${window.location.origin}`;
      } else if (code === "domain_not_allowed") {
        friendly = msg;
      } else if (msg.includes("AADSTS50020") || msg.includes("AADSTS500113")) {
        friendly = "Your Microsoft account exists but isn't a member of the IDWTEAM tenant. Contact your administrator.";
      } else if (code) {
        // Generic fallback for unexpected MSAL errors during redirect init.
        friendly = `Sign-in failed (${code}). If this persists, verify ${window.location.origin} is registered in the Entra app under Authentication → Single-page application.`;
      }

      setErrorMsg(friendly);
      setErrorCode(code);
      setStage("error");
    }
  }

  function backToEmail() {
    setStage("email");
    setErrorMsg("");
    setErrorCode("");
  }

  return (
    <div className="min-h-screen flex items-stretch bg-app font-body">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 w-2/5 relative bg-surface border-r border-app">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 15% 85%, rgba(220,40,20,0.08), transparent 45%), " +
              "radial-gradient(circle at 90% 10%, rgba(11,11,11,0.04), transparent 50%)"
          }}
        />
        <div className="relative">
          <div className="flex items-center gap-3 mb-6">
            <img src="/idwteam-logo.png" alt="IDWTEAM" className="h-10 w-auto" />
            <div>
              <div className="font-display text-xl">DataQ.Agent</div>
              <div className="text-10 uppercase tracking-xwide text-ink-mute">by IDWTEAM</div>
            </div>
          </div>
          <h1 className="font-display text-5xl leading-tight mb-4">
            Master data quality,
            <br />
            at SAP scale.
          </h1>
          <p className="text-ink-soft text-base max-w-md leading-relaxed">
            484 pre-built rules · five ML detectors · domain-aware dashboards. Sign in with your IDWTEAM account to continue.
          </p>
        </div>
        <div className="text-10 uppercase tracking-xwide text-ink-mute relative">
          IDWTEAM LLC · Alpharetta · Hyderabad
        </div>
      </div>

      {/* Right sign-in panel */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/idwteam-logo.png" alt="IDWTEAM" className="h-8 w-auto" />
            <div className="font-display text-xl">DataQ.Agent</div>
          </div>

          {/* Stage: email entry */}
          {stage === "email" && (
            <>
              <h2 className="font-display text-3xl mb-1.5">Sign in</h2>
              <p className="text-sm text-ink-soft mb-8">
                Enter your IDWTEAM email to continue with Microsoft.
              </p>

              <form onSubmit={handleSubmit} noValidate>
                <label htmlFor="email" className="block text-xs font-medium text-ink-soft mb-1.5">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  autoFocus
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError("");
                  }}
                  placeholder="you@idwteam.com"
                  className={`w-full px-3.5 py-2.5 border rounded-lg text-sm bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent-tint transition-colors ${
                    emailError ? "border-orange-500" : "border-app-strong focus:border-accent"
                  }`}
                />
                {emailError && (
                  <div className="mt-2 text-xs text-orange-700 flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>{emailError}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!email.trim()}
                  className="mt-5 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-accent hover:bg-accent-dark disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>

              <div className="mt-8 pt-6 border-t border-app text-10 text-ink-mute leading-relaxed">
                <div className="flex items-start gap-1.5">
                  <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>
                    DataQ Agent authenticates against IDWTEAM's Microsoft tenant{" "}
                    <span className="font-mono">{TENANT_HINT}…</span>. Your password is verified
                    by Microsoft, never by IDWTEAM.
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Stage: waiting for Microsoft popup */}
          {stage === "signing-in" && (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-accent-tint mb-4">
                <Loader2 className="w-6 h-6 animate-spin text-accent" />
              </div>
              <h2 className="font-display text-2xl mb-2">Redirecting to Microsoft</h2>
              <p className="text-sm text-ink-soft mb-6 max-w-xs mx-auto">
                Taking you to Microsoft to sign in as{" "}
                <span className="font-mono text-ink">{email}</span>. You'll come right back here once you complete sign-in.
              </p>
              <button
                onClick={backToEmail}
                className="text-xs text-ink-mute hover:text-ink inline-flex items-center gap-1"
              >
                <ArrowLeft className="w-3 h-3" />
                Use a different email
              </button>
            </div>
          )}

          {/* Stage: error */}
          {stage === "error" && (
            <>
              <h2 className="font-display text-3xl mb-1.5">Sign in failed</h2>
              <p className="text-sm text-ink-soft mb-6">
                Microsoft couldn't verify your account.
              </p>
              <div className="bg-orange-50 border border-orange-200 border-l-4 border-l-orange-500 rounded-lg p-4 text-sm mb-4">
                <div className="font-medium mb-1 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-700" />
                  What happened
                </div>
                <div className="text-ink-soft mb-2 leading-relaxed">{errorMsg}</div>
                {errorCode && errorCode !== "domain_not_allowed" && (
                  <div className="font-mono text-10 bg-orange-100 px-1.5 py-0.5 rounded text-orange-900 inline-block">
                    code: {errorCode}
                  </div>
                )}
              </div>
              <button
                onClick={backToEmail}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-app-strong bg-surface hover:bg-surface-2 rounded-lg text-sm font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Try a different email
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
